import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Plus, 
  Edit,
  Trash2,
  Users,
  Save,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Segments = () => {
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingSegment, setEditingSegment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ name: '', profile: '' });

  useEffect(() => {
    fetchSegments();
  }, []);

  const fetchSegments = async () => {
    try {
      const response = await axios.get(`${API}/segments`, { withCredentials: true });
      setSegments(response.data);
    } catch (error) {
      toast.error('Failed to load segments');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Segment name is required');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/segments`, formData, { withCredentials: true });
      toast.success('Segment created');
      setCreateOpen(false);
      setFormData({ name: '', profile: '' });
      fetchSegments();
    } catch (error) {
      toast.error('Failed to create segment');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.name.trim()) {
      toast.error('Segment name is required');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/segments/${editingSegment.segment_id}`, formData, { withCredentials: true });
      toast.success('Segment updated');
      setEditingSegment(null);
      setFormData({ name: '', profile: '' });
      fetchSegments();
    } catch (error) {
      toast.error('Failed to update segment');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (segmentId) => {
    if (!window.confirm('Are you sure you want to delete this segment?')) return;

    try {
      await axios.delete(`${API}/segments/${segmentId}`, { withCredentials: true });
      toast.success('Segment deleted');
      fetchSegments();
    } catch (error) {
      toast.error('Failed to delete segment');
    }
  };

  const openEdit = (segment) => {
    setEditingSegment(segment);
    setFormData({ name: segment.name, profile: segment.profile });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-forest"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="segments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground">Segments</h1>
          <p className="text-muted-foreground mt-1">Define customer profiles for personalized outreach</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-wheat hover:bg-wheat-dark text-white" data-testid="create-segment-btn">
              <Plus className="mr-2 h-4 w-4" />
              New Segment
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">Create Segment</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="name">Segment Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Fine Dining Restaurants"
                  data-testid="input-segment-name"
                />
              </div>
              <div>
                <Label htmlFor="profile">Segment Profile</Label>
                <Textarea
                  id="profile"
                  value={formData.profile}
                  onChange={(e) => setFormData({ ...formData, profile: e.target.value })}
                  placeholder="Describe who they are, what they care about, and what matters to them in a bakery supplier relationship..."
                  rows={6}
                  data-testid="input-segment-profile"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Claude uses this profile to write personalized outreach that resonates with this segment.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleCreate}
                disabled={saving}
                className="bg-forest hover:bg-forest-dark text-white"
                data-testid="submit-segment-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Segment'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Segments List */}
      <div className="grid gap-4">
        {segments.map((segment) => (
          <Card key={segment.segment_id} className="bg-white border-stone-100" data-testid={`segment-${segment.segment_id}`}>
            <CardHeader className="flex flex-row items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-sage rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users className="h-5 w-5 text-forest" />
                </div>
                <div>
                  <CardTitle className="font-heading font-semibold text-lg">{segment.name}</CardTitle>
                  <CardDescription className="mt-1 flex items-center gap-3">
                    <span>Updated {new Date(segment.updated_at).toLocaleDateString()}</span>
                    {segment.lead_count > 0 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-forest/10 text-forest">
                        {segment.lead_count} {segment.lead_count === 1 ? 'lead' : 'leads'}
                      </span>
                    )}
                  </CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => openEdit(segment)}
                  data-testid={`edit-segment-${segment.segment_id}`}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => handleDelete(segment.segment_id)}
                  className="text-destructive hover:text-destructive"
                  data-testid={`delete-segment-${segment.segment_id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {segment.profile || 'No profile defined yet.'}
              </p>
            </CardContent>
          </Card>
        ))}

        {segments.length === 0 && (
          <Card className="bg-white border-stone-100">
            <CardContent className="text-center py-12">
              <Users className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="font-heading font-semibold text-lg mb-2">No segments yet</h3>
              <p className="text-muted-foreground mb-4">Create segments to personalize your outreach</p>
              <Button onClick={() => setCreateOpen(true)} className="bg-forest hover:bg-forest-dark text-white">
                <Plus className="mr-2 h-4 w-4" />
                Create Segment
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingSegment} onOpenChange={(open) => !open && setEditingSegment(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Segment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-name">Segment Name *</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-profile">Segment Profile</Label>
              <Textarea
                id="edit-profile"
                value={formData.profile}
                onChange={(e) => setFormData({ ...formData, profile: e.target.value })}
                rows={8}
              />
              <p className="text-xs text-muted-foreground mt-2">
                Claude uses this profile to write personalized outreach that resonates with this segment.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingSegment(null)}>Cancel</Button>
            <Button 
              onClick={handleUpdate}
              disabled={saving}
              className="bg-forest hover:bg-forest-dark text-white"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Segments;
